import React from 'react';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button } from '@components/atoms/Button';
import { AssistantActionType, AssistantPendingAction } from '@models/assistant.model';
import { useTranslation } from '@hooks/useTranslation';

interface ActionCardProps {
  pendingAction: AssistantPendingAction;
  onConfirm: () => void;
  onDismiss: () => void;
  busy?: boolean;
}

const ACTION_LABEL_KEY: Record<AssistantActionType, string> = {
  ADD_NOTE: 'actionAddNote',
  REPORT_INCIDENT: 'actionReportIncident',
  CHANGE_TASK_STATUS: 'actionChangeTaskStatus',
  ACKNOWLEDGE_INCIDENT: 'actionAcknowledgeIncident',
  SHIFT_TASK: 'actionShiftTask',
  ASSIGN_CREW_TO_TASK: 'actionAssignCrewToTask',
  ASSIGN_EMPLOYEE_TO_CREW: 'actionAssignEmployeeToCrew',
};

/** One detail line beyond `humanSummary`, per `docs/ux/story-165-design.md`'s Action Card table. */
const DetailLine: React.FC<{ pendingAction: AssistantPendingAction }> = ({ pendingAction }) => {
  const args = pendingAction.args as Record<string, unknown>;
  switch (pendingAction.type) {
    case 'CHANGE_TASK_STATUS':
      return (
        <div className="flex items-center gap-2 text-xs mt-1">
          <span className="px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700">
            {pendingAction.expectedState?.taskStatus ?? '—'}
          </span>
          <ArrowForwardIcon sx={{ fontSize: 14 }} className="text-gray-400" />
          <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary">
            {(args.newStatus as string) ?? '—'}
          </span>
        </div>
      );
    case 'REPORT_INCIDENT':
      return args.incidentType ? (
        <div className="text-xs mt-1">
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">{args.incidentType as string}</span>
        </div>
      ) : null;
    default:
      return null;
  }
};

/** Sprint 32, story-165. Renders when `pendingAction` is set — `humanSummary` + a typed detail
 * block per `AssistantActionType` + Confirm/Dismiss. Buttons disable themselves immediately on
 * tap so a double-tap can't fire the mutation twice (mirrors story-161's commit-idempotency rule). */
export const ActionCard: React.FC<ActionCardProps> = ({ pendingAction, onConfirm, onDismiss, busy }) => {
  const t = useTranslation('assistant');
  const label = t[ACTION_LABEL_KEY[pendingAction.type] as keyof typeof t] as string;

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-800"
      data-testid="assistant-action-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
        <FlagOutlinedIcon sx={{ fontSize: 16 }} />
        <span>{label}</span>
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{pendingAction.humanSummary}</p>
      <DetailLine pendingAction={pendingAction} />
      <div className="flex justify-end gap-2 mt-3">
        <Button
          type="button"
          variant="secondary"
          size="md"
          disabled={busy}
          onClick={onDismiss}
          data-testid="assistant-action-dismiss">
          {t.dismiss}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={busy}
          onClick={onConfirm}
          data-testid="assistant-action-confirm">
          {t.confirm}
        </Button>
      </div>
    </div>
  );
};

export default ActionCard;
